import { useState, type JSX } from 'react'
import { KeyRound, X, Copy, Check } from 'lucide-react'

const R = 'rounded-[3px]'
const BTN_PRIMARY = `flex h-9 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`
const BTN_SECONDARY = `flex h-9 items-center gap-1.5 ${R} border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40`

interface MfaBackupCodesDialogProps {
  codes: string[]
  onClose: () => void
}

export function MfaBackupCodesDialog({
  codes,
  onClose,
}: MfaBackupCodesDialogProps): JSX.Element {
  const [copied, setCopied] = useState(false)

  async function copyAll(): Promise<void> {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Backup codes"
    >
      <div className={`w-full max-w-md border border-slate-200 bg-white p-6 shadow-xl ${R}`}>
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}
            >
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">
                Backup codes
              </h3>
              <p className="mt-0.5 text-[12.5px] text-red-600">
                Save these codes now. They will not be shown again.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`${R} p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <pre
          className={`max-h-60 overflow-auto border border-slate-200 bg-slate-50 p-3 text-sm font-mono ${R}`}
        >
          {codes.join('\n')}
        </pre>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={BTN_PRIMARY} onClick={copyAll}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy all'}
          </button>
          <button type="button" className={BTN_SECONDARY} onClick={onClose}>
            <Check className="h-4 w-4" />
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
